<template>
  <div v-if="loading || visibleBarns.length > 0" class="farm-silos-group">
    <div class="group-header">
      <h2 class="group-title">{{ farm.name }}</h2>
      <img src="../assets/silo.svg" alt="" class="feed-icon" />
    </div>

    <div v-if="loading && dataBarns.length === 0" class="loading-container">
      <AnimadePlaceHolder height="120px" width="100%" borderRadius="4px"/>
    </div>

    <div v-if="visibleBarns.length > 0" class="group-body">
      <BarnSilos 
        v-for="barn in visibleBarns" 
        :key="barn.pk"
        :barn="barn"
        :farm="farm"
        :hide-header="isSingleMatchingBarn(barn)"
      />
    </div>
  </div>
</template>

<script>
import BarnSilos from './BarnSilos.vue';
import AnimadePlaceHolder from './AnimadePlaceHolder.vue';
import { apiRoutes } from "../utils/api";
import { filterSiloHomeBarns } from '../utils/siloHomeVisibility';

export default {
  name: 'FarmSilosGroup',
  components: {
    BarnSilos,
    AnimadePlaceHolder
  },
  props: {
    farm: {
      type: Object,
      required: true
    },
    company: {
      type: Number,
      required: true
    },
    deferLoad: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dataBarns: [],
      loading: true,
    }
  },
  computed: {
    companySettings() {
      return this.$store.getters.getCompanySettings;
    },
    visibleBarns() {
      return filterSiloHomeBarns(this.companySettings, this.farm, this.dataBarns);
    },
  },
  methods: {
    isSingleMatchingBarn(barn) {
      if (this.dataBarns.length !== 1) return false;
      const farmName = (this.farm?.name || '').trim().toLowerCase();
      const barnName = (barn?.name || '').trim().toLowerCase();
      return farmName === barnName;
    },
    getFarmPk() {
      return this.farm?.farm?.pk ?? this.farm?.farm ?? this.farm?.pk ?? null;
    },
    applyCachedBarns() {
      const farmPk = this.getFarmPk();
      const cachedBarns = this.$store.getters.getCachedHomeFarmBarns(farmPk);
      if (!cachedBarns || cachedBarns.length === 0) return false;
      this.dataBarns = cachedBarns;
      this.loading = false;
      return true;
    },
    async fetchBarns({ background = false } = {}) {
      try {
        if (!background) {
          this.loading = true;
        }
        let url_to_fetch = apiRoutes.farmHomeByFarm(this.farm.farm);
        const response = await fetch(url_to_fetch, {
          headers: this.$store.getters.getHeader
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const json = await response.json();
        this.dataBarns = json;
        this.$store.commit('setHomeFarmBarnsCache', {
          farmPk: this.getFarmPk(),
          barns: json,
          lastUpdated: new Date().toISOString()
        });
 
        const barn = {
          farm: this.farm,
          barn: json
        };

        if (!this.farm.company) {
          this.farm.company = this.company
        }
        this.$store.commit('setAllBarns', barn);
      } catch (err) {
        console.error('Error fetching barns:', err);
        this.dataBarns = [];
      } finally {
        this.loading = false;
      }
    },
    scheduleInitialFetch() {
      const run = () => {
        void this.fetchBarns();
      };

      if (this.deferLoad) {
        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(run, { timeout: 2500 });
        } else {
          setTimeout(run, 300);
        }
        return;
      }

      run();
    },
  },
  watch: {
    async farm() {
      const hasCache = this.applyCachedBarns();
      if (hasCache) {
        this.fetchBarns({ background: true });
        return;
      }
      this.scheduleInitialFetch();
    }
  },
  created() {
    const hasCache = this.applyCachedBarns();
    if (hasCache) {
      this.fetchBarns({ background: true });
      return;
    }
    this.scheduleInitialFetch();
  }
};
</script>

<style scoped>
.farm-silos-group {
  margin-bottom: 24px;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Header del Grupo */
.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.group-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.group-body {
  padding: 0;
}

.loading-container {
  padding: 12px 20px;
}

.feed-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) saturate(100%);
  flex-shrink: 0;
}

/* Sección de Farm */
.farm-section {
  padding: 20px 24px;
  background-color: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.farm-section:last-child {
  border-bottom: none;
}

.farm-title-container {
  margin-bottom: 16px;
}

.farm-title {
  font-size: 18px;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

/* Grid de Silos Completos */
.silos-full-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

@media screen and (min-width: 768px) {
  .silos-full-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (min-width: 1024px) {
  .silos-full-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media screen and (min-width: 1400px) {
  .silos-full-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>

